import isEmpty from 'lodash/isEmpty';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import APIUtils from '@livongo/utils/api';

export const PREFIX = 'Programs';
export const FETCH_PROGRAMS = `${PREFIX}/fetchPrograms`;
export const PROGRAM_UPSELL = `${PREFIX}/programUpsell`;
export const HYPERTENSION_REGISTRATION = `${PREFIX}/hypertensionRegistration`;
export const CLIENT_PROFILE = `${PREFIX}/clientProfile`;

export const fetchPrograms = createAsyncThunk(
    FETCH_PROGRAMS,
    async (program, {dispatch, getState, rejectWithValue}) => {
        const {authToken} = getState().user;

        try {
            const response = await APIUtils.get(
                `/v1/users/me/programs/v2`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            const programs = response.data;

            return programs;
        } catch (err) {
            const {data} = err;

            if (data.error) {
                return rejectWithValue({error: data.error});
            } else if (data.message) {
                return rejectWithValue({error: data.message});
            }
        }
    }
);
export const programUpsell = createAsyncThunk(
    PROGRAM_UPSELL,
    async (parameters, {dispatch, getState, rejectWithValue}) => {
        const {authToken} = getState().user;
        const basicAuthConfig = {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        };

        try {
            /* Support multi-program
            const {programs, answers} = parameters;

            const response = await APIUtils.post(
                '/v1/users/me/solution',
                {
                    programs,
                    answers,
                },
                basicAuthConfig
            );

            return programs;
            */

            const {program, answers, auxiliaryHealthInfo} = parameters;

            const requestPayload = isEmpty(auxiliaryHealthInfo)
                ? {
                      program,
                      answers,
                  }
                : {
                      program,
                      answers,
                      auxiliaryHealthInfo,
                  };

            await APIUtils.post(
                '/v1/users/me/programUpsell',
                requestPayload,
                basicAuthConfig
            );

            return program;
        } catch (err) {
            const {data} = err;

            // eslint-disable-next-line no-console
            console.log('Program Upsell Error:', data);

            if (data.error) {
                return rejectWithValue({error: data.error});
            } else if (data.message) {
                if (
                    data.validations &&
                    Object.prototype.hasOwnProperty.call(
                        data.validations,
                        'errors'
                    )
                ) {
                    return rejectWithValue({
                        error: data.message,
                        validations: data.validations.errors,
                    });
                } else if (data.details) {
                    return rejectWithValue({
                        error: data.message,
                        validations: [{message: data.details}],
                    });
                }

                return rejectWithValue({error: data.message});
            }
        }
    }
);
export const clientProfile = createAsyncThunk(
    CLIENT_PROFILE,
    async (parameters, {getState, rejectWithValue}) => {
        const {authToken} = getState().user;

        try {
            const {clientCode, program} = parameters;

            const response = await APIUtils.get(
                `/v1/clients/${clientCode}/getAllSubProfilesAssociatedToClient`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );
            // eslint-disable-next-line no-shadow
            const clientProfile = response.data;

            return {
                program,
                ...clientProfile,
            };
        } catch (err) {
            const {data} = err;

            // eslint-disable-next-line no-console
            console.log('Client Profile Error:', data);

            return rejectWithValue({error: data.error});
        }
    }
);
export const hypertensionRegistration = createAsyncThunk(
    HYPERTENSION_REGISTRATION,
    async (statusText, {dispatch, getState, rejectWithValue}) => {
        const {authToken} = getState().user;

        try {
            const response = await APIUtils.post(
                `/v1/users/me/hypertension/registration`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            return response.statusText;
        } catch (err) {
            const {data} = err;
            const errMessage = data.error_description || data.message;

            if (errMessage) {
                return rejectWithValue({error: errMessage});
            }
        }
    }
);

const initialState = {
    programs: [],
    programsSuccessfullyEnrolled: [
        // 'DIABETES',
        // 'HYPERTENSION',
        // 'BEHAVIORAL_HEALTH',
        // 'WEIGHT_MANAGEMENT',
        // 'PREDIABETES',
        // 'CHRONIC_KIDNEY_DISEASE',
        // 'HEART_FAILURE',
        // 'DIABETES_DEVICEFLEX',
    ],
    loading: false,
    error: null,
    validations: null,
    statusText: null,
    ckdDiagnosis: false,
};

// Then, handle actions in your reducers:
const questionsSlice = createSlice({
    name: PREFIX,
    initialState,
    reducers: {
        // standard reducer logic, with auto-generated action types per reducer
    },
    extraReducers: {
        [fetchPrograms.pending]: (state, action) => {
            state.loading = true;
        },
        [fetchPrograms.fulfilled]: (state, action) => {
            const programs = action.payload;

            state.programs = programs;
            state.loading = false;
        },
        [fetchPrograms.rejected]: (state, action) => {
            const {error} = action.payload;

            state.loading = false;
            state.error = error;
        },
        [programUpsell.pending]: (state, action) => {
            state.loading = true;
        },
        [programUpsell.fulfilled]: (state, action) => {
            const programsSuccessfullyEnrolled = action.payload;

            state.loading = false;
            // state.programsSuccessfullyEnrolled = programsSuccessfullyEnrolled; Support Multi-program
            state.programsSuccessfullyEnrolled = [programsSuccessfullyEnrolled];
        },
        [programUpsell.rejected]: (state, action) => {
            const {error, validations} = action.payload;

            state.loading = false;
            state.error = error;
            state.validations = validations;
        },
        [hypertensionRegistration.fulfilled]: (state, action) => {
            state.statusText = action.payload;
        },
        [hypertensionRegistration.rejected]: (state, action) => {
            state.error = action.payload;
        },
        [clientProfile.fulfilled]: (state, action) => {
            const {platformFeatureSubProfiles, program} = action.payload;
            let programType = program;

            // Handling flavored CKD in other programs i.e. client code HUMANA has flavored CKD program in DBT and HTN.
            if (platformFeatureSubProfiles) {
                programType = platformFeatureSubProfiles.filter(profile => {
                    return profile.program === program;
                })[0].name;
            }

            state.ckdDiagnosis =
                programType === 'CHRONIC_KIDNEY_DISEASE' ||
                programType === 'CKD';
        },
        [clientProfile.rejected]: (state, action) => {
            // eslint-disable-next-line camelcase
            const {error, error_description} = action.payload;

            state.error = error;
            // eslint-disable-next-line camelcase
            state.validations = error_description;
        },
    },
});

export default questionsSlice.reducer;
